import React, { useCallback } from 'react';
import { format, addDays, startOfWeek, endOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarProps, CalendarView } from './types';
import { Button } from '../UI/Button';
import DayView from './DayView';
import WeekView from './WeekView';
import MonthView from './MonthView';

const Calendar: React.FC<CalendarProps> = ({
  view,
  currentDate,
  appointments,
  workingSettings,
  denseMode = true,
  onDateChange,
  onViewChange,
  onAppointmentClick,
  onTimeSlotClick,
  isLoading = false
}) => {
  // Fonctions de navigation
  const goToPrevious = useCallback(() => {
    let newDate: Date;
    switch (view) {
      case 'day':
        newDate = addDays(currentDate, -1);
        break;
      case 'week':
        newDate = addDays(currentDate, -7);
        break;
      case 'month':
        newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        break;
      default:
        newDate = currentDate;
    }
    onDateChange(newDate);
  }, [view, currentDate, onDateChange]);

  const goToNext = useCallback(() => {
    let newDate: Date;
    switch (view) {
      case 'day':
        newDate = addDays(currentDate, 1);
        break;
      case 'week':
        newDate = addDays(currentDate, 7);
        break;
      case 'month':
        newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
        break;
      default:
        newDate = currentDate;
    }
    onDateChange(newDate);
  }, [view, currentDate, onDateChange]);

  const goToToday = useCallback(() => {
    onDateChange(new Date());
  }, [onDateChange]);

  // Formatage de la date selon la vue
  const getFormattedDate = () => {
    switch (view) {
      case 'day':
        return format(currentDate, 'EEEE d MMMM yyyy', { locale: fr });
      case 'week': {
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
        return `${format(weekStart, 'd MMM', { locale: fr })} - ${format(weekEnd, 'd MMM yyyy', { locale: fr })}`;
      }
      case 'month':
        return format(currentDate, 'MMMM yyyy', { locale: fr });
      default:
        return '';
    }
  };

  // Rendu du composant de vue approprié
  const renderCalendarView = () => {
    const commonProps = {
      currentDate,
      appointments,
      workingSettings,
      denseMode,
      onAppointmentClick,
      onTimeSlotClick,
      isLoading
    };

    switch (view) {
      case 'day':
        return <DayView {...commonProps} />;
      case 'week':
        return <WeekView {...commonProps} />;
      case 'month':
        return <MonthView {...commonProps} />;
      default:
        return <DayView {...commonProps} />;
    }
  };

  return (
    <div className="w-full h-full flex flex-col">
      {/* En-tête du calendrier */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        {/* Navigation de date */}
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={goToPrevious}
            disabled={isLoading}
          >
            ←
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={goToNext}
            disabled={isLoading}
          >
            →
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={goToToday}
            disabled={isLoading}
          >
            Aujourd'hui
          </Button>
        </div>

        {/* Titre avec la date */}
        <h2 className="text-xl font-semibold text-gray-900 capitalize">
          {getFormattedDate()}
        </h2>

        {/* Sélecteur de vue */}
        <div className="flex items-center space-x-2">
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            {(['day', 'week', 'month'] as CalendarView[]).map((viewOption) => (
              <Button
                key={viewOption}
                variant={view === viewOption ? 'primary' : 'outline'}
                size="sm"
                onClick={() => onViewChange(viewOption)}
                disabled={isLoading}
                className={`rounded-none first:rounded-l-lg last:rounded-r-lg border-0 ${
                  view === viewOption 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {viewOption === 'day' && 'Jour'}
                {viewOption === 'week' && 'Semaine'}
                {viewOption === 'month' && 'Mois'}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Contenu du calendrier */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex items-center space-x-2 text-gray-500">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span>Chargement...</span>
            </div>
          </div>
        ) : (
          renderCalendarView()
        )}
      </div>
    </div>
  );
};

export default Calendar;